import { Command, CommandItem } from "@comps/ui/command";
import { Label } from "@comps/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@comps/ui/select";
import { TreeDataItem, TreeView } from "@comps/ui/tree-view";
import { KPIData, KPIWidgetType, Widget } from "@dto/Dashboard";
import Field, { AggregationType, validNumericAggregationByType } from "@dto/fields";
import { ObjectInfos } from "@dto/objects";
import { Filter } from "@dto/records";
import { fieldToPath, pathKey, pathKeyToPath } from "@utils/model";
import { buildFieldTree } from "@utils/tree";
import { TW_DashboardCardFieldArrow, TW_DashboardCardFieldSelected, TW_DashboardCardFieldText, TW_DashboardCardTreeView, TW_DashboardSection_Arrow, TW_DashboardSection_Label, TW_DashboardSection_ScrollArea, TW_SharedObjectsCollapsibleTrigger, TW_WidgetSectionLabel, TW_WidgetSpecificSection } from "@utils/twClasses";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useModelStore } from "src/store";
import { WidgetCardConfigProps } from "./WidgetConfig";
import WidgetFilterEditor from "./WidgetFilterEditor";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@comps/ui/collapsible";
import { ScrollArea } from "@comps/ui/scroll";


export function KPICardConfig({ initialWidget, onConfigChange }: WidgetCardConfigProps) {

    const objectInfos = useModelStore(state => state.objectInfos);
    const sharedObjectInfos = useModelStore(state => state.sharedObjectInfos);
    const [selectedObject, setSelectedObject] = useState<ObjectInfos | undefined>(undefined);
    const [selectedField, setSelectedField] = useState<Field | undefined>(undefined);
    const [selectedAggregation, setSelectedAggregation] = useState<AggregationType | undefined>(undefined);
    const [selectedFilter, setSelectedFilter] = useState<Filter | null>(null);
    const [fieldAggregationByPathKeys, setFieldAggregationByPathKeys] = useState<Map<string, AggregationType | undefined>>(new Map<string, AggregationType | undefined>());
    const fieldByKeys = useModelStore(state => state.fieldByKeys);
    const relationByKeys = useModelStore(state => state.relationByKeys);
    const objectByKeys = useModelStore(state => state.objectByKeys);
    const workspaceByKeys = useModelStore(state => state.workspaceByKeys);
    const [isSharedObjectsOpen, setIsSharedObjectsOpen] = useState(false);

    const { t } = useTranslation();

    useEffect(() => {
        if (initialWidget?.data) {
            const data = initialWidget.data as KPIData;
            if (data.valuePath) {
                const path = pathKeyToPath(data.valuePath, relationByKeys);
                if (path) {
                    setSelectedField(fieldByKeys?.get(path.field));
                    setSelectedObject(objectByKeys?.get(path.object));
                }
                setSelectedAggregation(data.valueAggregation ?? undefined);
            }
            if (data.filter) {
                setSelectedFilter(data.filter);
            }
        }
    }, [initialWidget]);

    function updateFieldAggregationByPathKeys(fieldAggregation: AggregationType | undefined) {
        const newFieldAggregationByPathKeys = new Map<string, AggregationType | undefined>();

        if (fieldAggregation && selectedField?._key) {
            const valuePath = (selectedObject?.object?._key && selectedField) ? fieldToPath(selectedObject.object._key, relationByKeys, fieldByKeys, selectedField) : null;
            const valuePathKey = valuePath ? pathKey(valuePath) : null;
            if (valuePathKey) {
                newFieldAggregationByPathKeys.set(valuePathKey, fieldAggregation);
            }
        }
        setFieldAggregationByPathKeys(newFieldAggregationByPathKeys);
    }

    function getNewWidget(objectKey: string | undefined, field: Field | undefined, aggregation: AggregationType | undefined, filter: Filter | null): Widget | undefined {
        if (!objectKey || !field || !aggregation) {
            return undefined;
        }
        const valuePath = (objectKey && field) ? fieldToPath(objectKey, relationByKeys, fieldByKeys, field) : null;
        const valuePathKey = valuePath ? pathKey(valuePath) : null;
        const newWidget: Widget = {
            type: KPIWidgetType,
            title: initialWidget?.title || "KPI", // Overridden by WidgetConfig
            description: initialWidget?.description || "", // Overridden by WidgetConfig
            width: initialWidget?.width ?? null,  // Overridden by WidgetConfig
            height: initialWidget?.height ?? null, // Overridden by WidgetConfig
            showLegend: false,
            showValues: initialWidget?.showValues ?? true, // Overridden by WidgetConfig
            showPercentage: initialWidget?.showPercentage ?? false, // Overridden by WidgetConfig
            hideYAxis: true, // Overridden by WidgetConfig
            data: new KPIData(valuePathKey, aggregation, filter)
        }
        return newWidget;
    }

    function onFieldChange(objectKey: string | undefined, fieldKey: string | undefined) {
        if (selectedField && selectedField._key === fieldKey) {
            return;
        }
        if (!objectKey || !fieldKey) {
            setSelectedField(undefined);
            setSelectedObject(undefined);
            setSelectedAggregation(undefined);
            setSelectedFilter(null);
            updateFieldAggregationByPathKeys(undefined);
            onConfigChange(undefined);
        } else {
            const field = fieldByKeys?.get(fieldKey);
            setSelectedField(field);
            let defaultAggr = undefined;
            const validAggrs = validNumericAggregationByType(field?.type, field?.typeItems);
            if (validAggrs.length > 0) {
                defaultAggr = validAggrs[0];
            }
            setSelectedObject(objectByKeys?.get(objectKey));
            setSelectedAggregation(defaultAggr);
            updateFieldAggregationByPathKeys(defaultAggr);
            setSelectedFilter(null);
            const newWidget = getNewWidget(objectKey, field, defaultAggr, null);
            if (newWidget) {
                onConfigChange(newWidget);
            }
        }
    }

    const onAggregationChange = (value: string | undefined) => {
        if (value) {
            setSelectedAggregation(value as AggregationType);
            const newWidget = getNewWidget(selectedObject?.object?._key, selectedField, value as AggregationType, selectedFilter);
            if (newWidget) {
                onConfigChange(newWidget);
            }
            updateFieldAggregationByPathKeys(value as AggregationType);
        } else {
            setSelectedAggregation(undefined);
            updateFieldAggregationByPathKeys(undefined);
            onConfigChange(undefined);
        }
    }

    const objectTree = buildFieldTree(objectInfos, onFieldChange);

    const sharedObjectTree: TreeDataItem[] = sharedObjectInfos?.map(ws => ({
        id: ws.wsKey,
        name: workspaceByKeys?.get(ws.wsKey)?.name ?? "",
        children: ws.objectInfos.map(obj => ({
            id: obj.object._key,
            name: obj.object.name,
            children: obj.fields?.map(field => ({
                id: field._key,
                name: field.name,
                onClick: () => onFieldChange(obj.object._key, field._key)
            }))
        })),
    }));


    const aggregationOptions = validNumericAggregationByType(selectedField?.type, selectedField?.typeItems);

    function onFilterChange(filter: Filter | null) {
        setSelectedFilter(filter);
        const newWidget = getNewWidget(selectedObject?.object?._key, selectedField, selectedAggregation, filter);
        if (newWidget) {
            onConfigChange(newWidget);
        }
    }
    return (
        <>
            <Label className={TW_WidgetSectionLabel}>{t("dashboard.configuration.component.label")}</Label>
            <div className={TW_WidgetSpecificSection}>
                <Label>{t("dashboard.value.field.label")}</Label>
                {!selectedField && objectTree && objectTree.length > 0 && (
                    <TreeView searchable data={objectTree} className={TW_DashboardCardTreeView} />
                )}
                {!selectedField && (!objectTree || objectTree.length === 0) && (
                    <span className={TW_DashboardCardFieldText}>{t("dashboard.value.field.no.objects")}</span>
                )}
                {!selectedField && sharedObjectTree && sharedObjectTree.length > 0 && (
                    <Collapsible open={isSharedObjectsOpen} onOpenChange={setIsSharedObjectsOpen} className="w-full">
                        <CollapsibleTrigger className={TW_SharedObjectsCollapsibleTrigger}>
                            <p className={TW_DashboardSection_Label}>{t(`dashboard.value.field.shared.objects.label`)}</p>
                            {isSharedObjectsOpen ? <ChevronDown className={TW_DashboardSection_Arrow} /> : <ChevronRight className={TW_DashboardSection_Arrow} />}
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                            <ScrollArea className={TW_DashboardSection_ScrollArea}>
                                <TreeView searchable data={sharedObjectTree} className={TW_DashboardCardTreeView} />
                            </ScrollArea>
                        </CollapsibleContent>
                    </Collapsible>
                )}
                {selectedField && selectedObject &&
                    <Command className="h-max">
                        <CommandItem
                            className={TW_DashboardCardFieldSelected}
                            onSelect={() => onFieldChange(undefined, undefined)}>
                            <span className={TW_DashboardCardFieldText}>{selectedObject?.object.name + " > " + selectedField.name}</span>
                            <ChevronDown className={TW_DashboardCardFieldArrow} />
                        </CommandItem>
                    </Command>
                }
                {selectedField && aggregationOptions.length > 0 && (
                    <>
                        <Label>{t("dashboard.value.aggregation.label")}</Label>
                        <Select value={selectedAggregation?.toString()} onValueChange={onAggregationChange}>
                            <SelectTrigger>
                                <SelectValue placeholder={t("dashboard.value.aggregation.placeholder")} />
                            </SelectTrigger>
                            <SelectContent>
                                {aggregationOptions.map(option => (
                                    <SelectItem key={option.toString()} value={option.toString()}>{t(`field.aggregation.label.${option}`)}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </>
                )}
            </div>
            {selectedField && selectedObject &&
                <div className={TW_WidgetSpecificSection}>
                    <Label className={TW_WidgetSectionLabel}>{t("dashboard.configuration.filter.label")}</Label>
                    <WidgetFilterEditor widget={initialWidget} srcObjectKey={selectedObject.object._key} onFilterChange={onFilterChange} fieldAggregationByPathKeys={fieldAggregationByPathKeys} />
                </div>
            }
        </>
    );
}